import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  useTheme
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { MonthlyReportData } from '../utils/types';
import { formatMoney } from '../utils/global';

type MonthlyReportChartProps = {
  data: MonthlyReportData[];
  chartType?: 'line' | 'bar' | 'pie';
  showJewelerBreakdown?: boolean;
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function MonthlyReportChart({ 
  data, 
  chartType = 'line',
  showJewelerBreakdown = false 
}: MonthlyReportChartProps) {
  const theme = useTheme();

  const chartData = data.map(item => ({
    month: item.monthName,
    revenue: item.revenue,
    profit: item.profit,
    transactionCount: item.transactionCount,
    avgTransaction: item.averageTransactionValue
  }));

  const pieData = data.map(item => ({
    name: item.monthName,
    value: item.revenue,
    profit: item.profit
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 2,
            boxShadow: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="body2">
                {entry.dataKey === 'revenue' && 'Ciro: '}
                {entry.dataKey === 'profit' && 'Kar: '}
                {entry.dataKey === 'transactionCount' && 'İşlem Sayısı: '}
                {entry.dataKey === 'avgTransaction' && 'Ort. İşlem: '}
                {entry.dataKey === 'transactionCount' || entry.dataKey === 'avgTransaction' 
                  ? entry.value 
                  : formatMoney(entry.value.toString())
                }
                {entry.dataKey === 'avgTransaction' && ' TL'}
                {entry.dataKey === 'transactionCount' && ' adet'}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenue" fill="#0088FE" name="Ciro" />
              <Bar dataKey="profit" fill="#00C49F" name="Kar" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.name} ${(entry.percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#0088FE" 
                strokeWidth={2}
                name="Ciro"
              />
              <Line 
                type="monotone" 
                dataKey="profit" 
                stroke="#00C49F" 
                strokeWidth={2}
                name="Kar"
              />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  const getTotalStats = () => {
    const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
    const totalProfit = data.reduce((sum, item) => sum + item.profit, 0);
    const totalTransactions = data.reduce((sum, item) => sum + item.transactionCount, 0);
    const avgTransactionValue = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

    return { totalRevenue, totalProfit, totalTransactions, avgTransactionValue };
  };

  const stats = getTotalStats();

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Aylık Rapor Grafiği
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              label="Çizgi" 
              color={chartType === 'line' ? 'primary' : 'default'}
              onClick={() => {/* Handle chart type change */}}
              size="small"
            />
            <Chip 
              label="Bar" 
              color={chartType === 'bar' ? 'primary' : 'default'}
              onClick={() => {/* Handle chart type change */}}
              size="small"
            />
            <Chip 
              label="Pasta" 
              color={chartType === 'pie' ? 'primary' : 'default'}
              onClick={() => {/* Handle chart type change */}}
              size="small"
            />
          </Box>
        </Box>

        {renderChart()}

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {formatMoney(stats.totalRevenue.toString())} TL
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Ciro
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                {formatMoney(stats.totalProfit.toString())} TL
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam Kar
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
                {stats.totalTransactions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toplam İşlem
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {formatMoney(stats.avgTransactionValue.toString())} TL
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ort. İşlem Değeri
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
